import React from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, MapPin, Info, MessageSquare, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";

interface RegularGatheringFallbacksProps {
    className?: string;
}

export const RegularGatheringFallbacks: React.FC<RegularGatheringFallbacksProps> = ({ className = "" }) => {
    const { t } = useTranslation();

    const gatherings = [
        {
            badge: t("events.regularGatheringBadge", "Regular Gathering"),
            title: t("events.sundayGatheringTitle", "Sunday Worship Gathering"),
            time: t("events.sundayGatheringTime", "Every Sunday at 10:00 AM"),
            location: t("events.sundayGatheringLocation", "Freemans Bay Community Centre, 52 Hepburn St"),
            actionLabel: t("home.planYourVisit", "Plan Your Visit"),
            actionTo: "/visit",
            mapsLink: "https://www.google.com/maps/search/?api=1&query=52+Hepburn+Street+Auckland",
        },
        {
            badge: t("events.regularGatheringBadge", "Regular Gathering"),
            title: t("events.midweekGatheringTitle", "Midweek Gathering & Bible Study"),
            time: t("events.midweekGatheringTime", "1st and 3rd Wednesdays at 7:00 PM"),
            location: t("events.midweekGatheringLocation", "Freemans Bay Community Centre / Small Groups"),
            actionLabel: t("events.getDirections", "Get Directions"),
            mapsLink: "https://www.google.com/maps/search/?api=1&query=52+Hepburn+Street+Auckland",
            actionTo: "/visit",
        },
    ];

    return (
        <div className={`space-y-6 ${className}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {gatherings.map((item, idx) => (
                    <div
                        key={idx}
                        className="bg-white dark:bg-warm-dark-surface rounded-2xl p-6 sm:p-7 border border-warm-border dark:border-warm-dark-border shadow-sm flex flex-col justify-between transition-all hover:shadow-md"
                    >
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                                    <Calendar className="w-5 h-5" aria-hidden="true" />
                                </div>
                                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300">
                                    {item.badge}
                                </span>
                            </div>

                            <h3 className="font-serif text-xl sm:text-2xl font-bold text-secondary dark:text-white mb-3">
                                {item.title}
                            </h3>

                            <div className="space-y-2.5 text-stone-600 dark:text-stone-300 text-sm mb-6">
                                <div className="flex items-center gap-2.5">
                                    <Clock className="w-4 h-4 text-primary shrink-0" aria-hidden="true" />
                                    <span className="font-medium">{item.time}</span>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <MapPin className="w-4 h-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                                    <span>{item.location}</span>
                                </div>
                            </div>
                        </div>

                        <div className="pt-2 flex flex-col sm:flex-row gap-3">
                            <Link
                                to={item.actionTo}
                                className="flex-1 text-center py-2.5 px-4 rounded-lg bg-primary text-white hover:bg-primary-hover text-sm font-semibold transition-colors"
                            >
                                {item.actionLabel}
                            </Link>
                            {item.mapsLink && (
                                <a
                                    href={item.mapsLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-center py-2.5 px-4 rounded-lg border border-warm-border dark:border-warm-dark-border text-stone-700 dark:text-stone-200 hover:bg-warm-stone/50 dark:hover:bg-warm-dark-bg text-sm font-medium transition-colors"
                                >
                                    {t("events.getDirections", "Get Directions")}
                                </a>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Confirmation Caveat Alert */}
            <div className="p-4 sm:p-5 rounded-2xl bg-amber-500/10 border border-amber-500/20 dark:border-amber-500/30 flex items-start gap-3.5 text-stone-700 dark:text-stone-200 text-sm">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 text-amber-700 dark:text-amber-400 flex items-center justify-center shrink-0 mt-0.5">
                    <Info className="w-4 h-4" aria-hidden="true" />
                </div>
                <div className="leading-relaxed">
                    <span>{t("events.caveatNotePre", "Gathering schedules or locations may occasionally vary. Please contact anyone from the church via ")}</span>
                    <a
                        href="https://m.me/409907289088953"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-semibold text-primary hover:underline underline-offset-2"
                    >
                        <MessageSquare className="w-3.5 h-3.5 inline-block" aria-hidden="true" />
                        <span>Facebook Messenger</span>
                    </a>
                    <span>{t("events.caveatNoteMid", " or email ")}</span>
                    <a
                        href="mailto:darvodelsky@gmail.com"
                        className="inline-flex items-center gap-1 font-semibold text-primary hover:underline underline-offset-2"
                    >
                        <Mail className="w-3.5 h-3.5 inline-block" aria-hidden="true" />
                        <span>darvodelsky@gmail.com</span>
                    </a>
                    <span>{t("events.caveatNotePost", " to confirm.")}</span>
                </div>
            </div>
        </div>
    );
};

export default RegularGatheringFallbacks;
